import * as THREE from 'three';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, keys, lerp, outBack, outCubic, outExpo, outQuad, phase, settle, span, type Ease } from '../curves.ts';
import { apply, timeline, type Beat, type KeyPose } from '../pose.ts';

/*
 * Portaestandarte de hueso: idle and walk (loops: the banner stirring, then swaying behind the rhythm of a
 * measured march), attack (a chop of the notched sword that lands at ~0.13 s; it starts cocked), hit, stun
 * (loop: the banner sags), die (it drops to its knees still holding the banner up, then pitches onto its face
 * reaching forward, and the banner swings back over it like a shroud) and its rally, `rally` (it plants the pole,
 * hauls the banner up high and the cloth snaps open).
 *
 * The pole is held upright in the world whatever the arm and trunk do (`aimPole`), so a body that leans and
 * turns never sweeps a pole twice its height around; its cloth hangs from the crossbar in three chained bands,
 * each swinging on the one above, so a wave runs down it: +X swings a band's lower edge back (as the wind of a
 * march would), −X forward.
 *
 * Conventions as in human.ts: limbs hang along −Y, so a negative X swings an arm or leg forward; +X on a shin
 * bends the knee back, −X on a forearm bends the elbow forward; +X on a trunk leans it forward; +X on a jaw
 * opens it; +Y turns a trunk to its right (+X); +Z on the right arm lifts it out to the side (−Z the left).
 */

type BB =
  | 'root' | 'aura' | 'hips' | 'tabard' | 'chest' | 'head' | 'jaw' | 'plume' | 'armL' | 'foreL' | 'pole'
  | 'bannerA' | 'bannerB' | 'bannerC' | 'trophyL' | 'trophyR' | 'armR' | 'foreR' | 'sword' | 'legL' | 'shinL' | 'legR' | 'shinR';
type BP = KeyPose<BB>;

/** A damped impulse at `t0` (0 before it). */
const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** Whole-figure squash from impulses: a landing flattens it a hair and it springs back. */
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** Held looks: a sine that lingers at its ends (a stare, then a slow turn). */
const held = (x: number, k = 0.5): number => {
  const s = Math.sin(x);
  return Math.sign(s) * Math.abs(s) ** k;
};

/** Adds the stance to every beat (so a timeline's beats only spell what changes from it). */
const over = (st: BP, beats: readonly (readonly [number, BP, Ease?])[]): Beat<BB>[] =>
  beats.map(([t, p, e]) => (e ? [t, { ...st, ...p }, e] : [t, { ...st, ...p }]) as Beat<BB>);

const _q = new THREE.Quaternion();
const _c = new THREE.Quaternion();
const _e = new THREE.Euler();
const _v = new THREE.Vector3();
const POLE_CHAIN: readonly BB[] = ['root', 'hips', 'chest', 'armL', 'foreL'];

/** The orientation of the left fist's frame (what the pole hangs in) from the clip's rotations so far. */
const fistFrame = (b: Bones<BB>): THREE.Quaternion => {
  _c.identity();
  for (const n of POLE_CHAIN) _c.multiply(_q.setFromEuler(_e.set(b[n].rx, b[n].ry, b[n].rz, 'XYZ')));
  return _c;
};

/**
 * Holds the pole (by `w`, blending with what the clip already set) at a world tilt: `tx` leans its top forward
 * (− back), `tz` toward the bearer's left (− right), `ty` turns the cloth's face; whatever the arm and trunk do.
 */
const aimPole = (b: Bones<BB>, tx: number, tz: number, w = 1, ty = 0): void => {
  if (w <= 0) return;
  const c = fistFrame(b).invert();
  c.multiply(_q.setFromEuler(_e.set(tx, ty, tz, 'XYZ')));
  _e.setFromQuaternion(c, 'XYZ');
  const p = b.pole;
  p.rx = lerp(p.rx, _e.x, w);
  p.ry = lerp(p.ry, _e.y, w);
  p.rz = lerp(p.rz, _e.z, w);
};

/** Slides the pole through the fist by a world-space offset (the butt planted while the body drops, a heave). */
const slidePole = (b: Bones<BB>, x: number, y: number, z: number): void => {
  if (x === 0 && y === 0 && z === 0) return;
  _v.set(x, y, z).applyQuaternion(fistFrame(b).invert());
  b.pole.pos(_v.x, _v.y, _v.z);
};

/** The cloth: each band's swing on the one above (+ back), with a little twist on the lower two. */
const cloth = (b: Bones<BB>, a: number, bb: number, c: number, twist = 0): void => {
  b.bannerA.rot(a);
  b.bannerB.rot(bb, twist * 0.5, 0);
  b.bannerC.rot(c, twist, 0);
};

/** Upright and proud: chest out, chin up, weight even; the banner arm's fist at its shoulder's height. */
const B_ST: BP = {
  root: [0, 0, 0, 0, -0.01, 0],
  hips: [0.0, 0, 0],
  chest: [-0.05, 0, 0],
  head: [-0.06, 0, 0],
  armR: [-0.05, 0, 0.04],
  foreR: [-0.12, 0, 0],
  legL: [-0.03, 0, -0.04],
  shinL: [0.05, 0, 0],
  legR: [0.03, 0, 0.04],
  shinR: [0.05, 0, 0],
};

/** The hanging bits trail the body. */
const LAG: Partial<Record<BB, number>> = { head: 0.03, jaw: 0.03, plume: 0.06, tabard: 0.06, foreR: 0.02, sword: 0.04 };

/** It stands its ground: a slow breath, a look round; the banner stirs in a wind only it feels. */
const idle = (): Clip<BB> =>
  clip<BB>('idle', 3.2, true, (b, t) => {
    const p = phase(t, 3.2) * TAU;
    apply(b, B_ST);
    const br = Math.sin(p * 2);
    b.root.pos(0, -0.008 * (1 + br), 0);
    b.chest.rot(-0.025 * br, 0.04 * Math.sin(p), 0.015 * Math.sin(p + 0.4));
    b.head.rot(0.03 * br, 0.28 * held(p + 2), 0.03 * Math.sin(p + 1));
    b.jaw.rot(0.06 * Math.max(0, Math.sin(p * 2 - 0.5)) ** 3);
    b.armR.rot(0.03 * br, 0, 0.02 * br);
    b.sword.rot(0.03 * Math.sin(p + 0.5));
    b.plume.rot(0.08 * Math.sin(p * 2 - 1), 0.15 * Math.sin(p - 0.8), 0);
    b.tabard.rot(0.04 * Math.sin(p * 2 - 1.2));
    b.aura.scale(1 + 0.04 * Math.sin(p * 2)).rot(0, 0.25 * Math.sin(p), 0);
    aimPole(b, 0.015 * Math.sin(p), 0.02 * Math.sin(p + 1));
    // Gusts run down the cloth: each band lags the one above; the tails flick.
    const gust = 0.5 + 0.5 * Math.sin(p - 0.6);
    cloth(b,
      0.07 + 0.06 * gust + 0.02 * Math.sin(p * 3),
      0.04 * Math.sin(p - 0.9) + 0.05 * gust + 0.03 * Math.sin(p * 3 - 1),
      0.07 * Math.sin(p - 1.8) + 0.06 * gust + 0.05 * Math.sin(p * 4 - 1.5),
      0.12 * Math.sin(p - 1.2) + 0.05 * Math.sin(p * 3 - 2));
    b.trophyL.rot(0.06 * Math.sin(p - 1.0), 0, 0.05 * Math.sin(p * 2 - 0.5));
    b.trophyR.rot(0.05 * Math.sin(p - 1.4), 0, -0.06 * Math.sin(p * 2 - 1.1));
  });

/**
 * A measured march: a slow, even stride on stiff knees, the sword arm barely swinging, the banner borne upright
 * and bobbing a beat behind each step, the cloth streaming back and rippling with the rhythm.
 */
const walk = (dur = 1.4): Clip<BB> =>
  clip<BB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    const land = Math.max(0, -Math.cos(2 * th - 0.3)) ** 4;
    apply(b, B_ST, 0.85);
    const A = 0.36;
    b.legL.rot(-A * sn, 0, -0.02);
    b.legR.rot(A * sn, 0, 0.02);
    b.shinL.rot(0.7 * Math.max(0, cs) ** 1.5 + 0.2 * Math.max(0, -sn) + 0.04 + 0.06 * land);
    b.shinR.rot(0.7 * Math.max(0, -cs) ** 1.5 + 0.2 * Math.max(0, sn) + 0.04 + 0.06 * land);
    b.root.pos(0.025 * Math.sin(th - 0.5), 0.025 * Math.cos(2 * th) - 0.015 - 0.02 * land, 0).rot(0, 0.04 * sn, -0.03 * Math.sin(th - 0.5));
    b.hips.rot(0.02 * land, 0.1 * sn, 0.02 * Math.sin(th - 0.5));
    b.chest.rot(0.03 + 0.025 * Math.cos(2 * th - 0.6), -0.12 * sn, 0.03 * Math.sin(th - 0.9));
    b.head.rot(-0.03 * Math.cos(2 * th - 1.0), 0.08 * sn, -0.03 * Math.sin(th - 1.1));
    b.armR.rot(-0.2 * Math.sin(th - 0.3), 0, 0.04);
    b.foreR.rot(-0.1 - 0.1 * Math.max(0, Math.sin(th - 0.6)));
    b.sword.rot(0.08 * Math.sin(th - 0.9));
    // The banner arm braces: the fist rides the step.
    b.armL.rot(0.03 * Math.cos(2 * th - 0.4), 0, 0.02 * land);
    b.plume.rot(0.15 + 0.12 * Math.sin(2 * th - 1.2), 0.1 * Math.sin(th - 1), 0);
    b.tabard.rot(0.15 * Math.sin(th - 1.2) + 0.06, 0, 0.04 * Math.sin(th - 1.5));
    b.aura.rot(0, -0.12 * sn, 0);
    // Borne upright, it sways side to side a beat behind the stride and dips on every footfall.
    aimPole(b, -0.04 + 0.025 * Math.sin(2 * th - 1.0), 0.05 * Math.sin(th - 1.1));
    slidePole(b, 0, -0.02 * land, 0);
    cloth(b,
      0.2 + 0.06 * Math.sin(2 * th - 1.4),
      0.12 + 0.09 * Math.sin(2 * th - 2.2),
      0.16 + 0.14 * Math.sin(2 * th - 3.0),
      0.14 * Math.sin(th - 1.8));
    b.trophyL.rot(0.12 + 0.22 * Math.sin(2 * th - 1.6), 0, 0.1 * Math.sin(th - 1.8));
    b.trophyR.rot(0.1 + 0.2 * Math.sin(2 * th - 2.0), 0, -0.1 * Math.sin(th - 2.1));
  }, (t) => {
    const k = Math.max(0, -Math.cos(phase(t, 1.4) * TAU * 2 - 0.3)) ** 4;
    return [1 + 0.015 * k, 1 - 0.02 * k, 1 + 0.015 * k];
  });

/**
 * The chop: it starts cocked, the notched sword drawn back over its right shoulder, and brings it down across
 * its front (0.13 s), the trunk turning behind the blow; the banner jolts on the impact and the cloth snaps.
 */
const A_COCK: BP = {
  root: [0, 0.1, 0, 0, -0.02, -0.02],
  hips: [0, 0.15, 0],
  chest: [-0.14, 0.4, 0.04],
  head: [-0.12, -0.3, 0],
  jaw: [0.2, 0, 0],
  armR: [-2.7, 0, 0.35],
  foreR: [-1.1, 0, 0],
  sword: [0.7, 0, 0],
  legL: [-0.2, 0, -0.05],
  shinL: [0.15, 0, 0],
  legR: [0.12, 0, 0.05],
  shinR: [0.15, 0, 0],
};
const A_CHOP: BP = {
  root: [0, -0.12, 0, 0, -0.06, 0.04],
  hips: [0.06, -0.15, 0],
  chest: [0.3, -0.35, -0.04],
  head: [0.12, 0.25, 0],
  jaw: [0.35, 0, 0],
  armR: [-1.2, 0, 0.12],
  foreR: [-0.15, 0, 0],
  sword: [-0.2, 0, 0],
  legL: [-0.38, 0, -0.05],
  shinL: [0.35, 0, 0],
  legR: [0.2, 0, 0.05],
  shinR: [0.3, 0, 0],
};
const A_THRU: BP = {
  ...A_CHOP,
  root: [0, -0.18, 0, 0, -0.07, 0.05],
  chest: [0.38, -0.5, -0.05],
  head: [0.1, 0.35, 0],
  armR: [-0.55, 0, -0.35],
  foreR: [-0.45, 0, 0],
  sword: [-0.1, 0, 0],
};

const attack = (): Clip<BB> =>
  clip<BB>('attack', 0.9, false, (b, t) => {
    timeline(b, t, over(B_ST, [
      [0, A_COCK],
      [0.13, A_CHOP, inCubic],
      [0.26, A_THRU, outCubic],
      [0.4, { ...A_THRU, chest: [0.36, -0.46, -0.05], armR: [-0.5, 0, -0.32] }],
      [0.9, {}, inOut],
    ]), LAG);
    const k = kick(t, 0.13, 4, 9);
    b.chest.rot(0.03 * k);
    b.armR.rot(0.06 * k);
    b.plume.rot(0.2 * span(t, 0, 0.13, inQuad) * (1 - span(t, 0.3, 0.9)) + 0.15 * kick(t, 0.15, 3, 7));
    b.tabard.rot(-0.1 * kick(t, 0.15, 2.5, 6));
    b.aura.scale(1 + 0.08 * Math.max(0, kick(t, 0.13, 2, 6)));
    // The banner holds; the impact jolts it and the cloth cracks like a whip down its length.
    const j = kick(t, 0.13, 2.4, 6);
    aimPole(b, 0.05 * j - 0.02 * span(t, 0, 0.13) * (1 - span(t, 0.4, 0.9)), -0.04 * j);
    cloth(b, 0.07 - 0.12 * j, 0.03 - 0.16 * kick(t, 0.17, 2.4, 6), 0.05 - 0.22 * kick(t, 0.21, 2.6, 6), 0.15 * kick(t, 0.19, 2, 6));
    b.trophyL.rot(-0.3 * kick(t, 0.16, 1.8, 5), 0, 0.15 * kick(t, 0.2, 1.8, 5));
    b.trophyR.rot(-0.25 * kick(t, 0.18, 1.8, 5), 0, -0.15 * kick(t, 0.22, 1.8, 5));
  }, (t) => thud(t, [[0.13, 0.025]]));

/** Struck: it rocks back a step, the helm snaps, the banner reels and the cloth cracks. */
const hit = (): Clip<BB> =>
  clip<BB>('hit', 0.45, false, (b, t) => {
    apply(b, B_ST);
    const k = settle(t, 2.2, 8) * span(t, 0, 0.03, outQuad);
    const a = Math.abs(k);
    b.chest.rot(-0.22 * k, 0.12 * k, 0.05 * k);
    b.hips.rot(-0.06 * k);
    b.head.rot(-0.4 * k, -0.15 * k);
    b.jaw.rot(0.35 * a);
    b.armR.rot(-0.25 * k, 0, 0.18 * a);
    b.foreR.rot(-0.2 * a);
    b.root.pos(0, -0.015 * a, -0.05 * k);
    b.shinL.rot(0.1 * a);
    b.shinR.rot(0.12 * a);
    const lag = settle(t - 0.06, 2, 6) * span(t, 0.03, 0.08);
    b.plume.rot(0.35 * lag);
    b.tabard.rot(0.25 * lag);
    aimPole(b, -0.07 * k, 0.04 * k);
    cloth(b, 0.07 + 0.2 * lag, 0.04 + 0.25 * settle(t - 0.1, 2, 6) * span(t, 0.06, 0.1), 0.06 + 0.3 * settle(t - 0.14, 2.2, 6) * span(t, 0.1, 0.14));
    b.trophyL.rot(0.35 * lag, 0, 0.1 * lag);
    b.trophyR.rot(0.3 * lag, 0, -0.12 * lag);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.03 * k, 1 - 0.04 * k, 1 + 0.03 * k];
  });

/** Dazed: it reels, the helm lolling; the banner sags out to its left, the cloth hanging limp. */
const stun = (): Clip<BB> =>
  clip<BB>('stun', 2, true, (b, t) => {
    const p = phase(t, 2) * TAU;
    apply(b, B_ST, 0.6);
    b.root.rot(0.03 * Math.sin(p), 0, 0.06 * Math.cos(p)).pos(0.03 * Math.cos(p), -0.04, 0);
    b.hips.rot(0.05, 0, -0.03 * Math.cos(p));
    b.chest.rot(0.25 + 0.05 * Math.sin(p * 2), 0.08 * Math.sin(p), -0.07 * Math.cos(p));
    b.head.rot(0.35 + 0.15 * Math.sin(p + 0.8), 0.3 * Math.cos(p + 0.8), 0.22 * Math.sin(p + 0.8));
    b.jaw.rot(0.3 + 0.08 * Math.sin(p * 2));
    b.armR.rot(0.12 * Math.sin(p + 1.2), 0, 0.04 * Math.cos(p + 1.4));
    b.foreR.rot(0.08);
    b.sword.rot(0.3);
    b.armL.rot(0.2, 0, 0.1);
    b.foreL.rot(0.25);
    b.shinL.rot(0.15);
    b.shinR.rot(0.15 + 0.08 * Math.sin(p));
    b.plume.rot(-0.15 + 0.1 * Math.sin(p + 2), 0.15 * Math.cos(p + 2), 0);
    b.tabard.rot(0.06 * Math.sin(p + 2));
    b.aura.scale(0.85 + 0.05 * Math.sin(p * 2));
    // The banner sags: the pole leans out and forward, swaying, and slides down through the slack fist.
    const tx = 0.22 + 0.06 * Math.sin(p - 0.8), tz = 0.3 + 0.08 * Math.cos(p - 0.8);
    aimPole(b, tx, tz);
    slidePole(b, 0, -0.06, 0);
    cloth(b, -tx * 0.9 + 0.03 * Math.sin(p - 1.4), 0.04 * Math.sin(p - 2), 0.06 * Math.sin(p - 2.6), 0.08 * Math.sin(p - 2));
    b.trophyL.rot(-tx + 0.1 * Math.sin(p - 1.6), 0, -tz * 0.9);
    b.trophyR.rot(-tx + 0.1 * Math.sin(p - 2), 0, -tz * 0.9);
  });

/**
 * Struck down: it reels back, sags, and its knees give; on its knees it still holds the banner up a long beat,
 * head bowed. Then it pitches onto its face, the banner hand flung forward, and the banner, swinging back over
 * it, comes down across its back like a shroud; the cloth slaps down and settles, and the ink at its feet dries.
 */
const die = (): Clip<BB> =>
  clip<BB>('die', 2.9, false, (b, t) => {
    const knees: BP = { legL: [0.05, 0, -0.1], shinL: [1.5, 0, 0], legR: [0.08, 0, 0.1], shinR: [1.55, 0, 0] };
    const kneel: BP = {
      ...knees, root: [0, 0, 0, 0, -0.44, -0.04], hips: [0.1, 0, 0], chest: [0.3, -0.1, 0.05], head: [0.55, 0.1, 0.1], jaw: [0.3, 0, 0],
      armL: [-0.15, 0, -0.12], foreL: [-0.2, 0, 0], armR: [0.15, 0, 0.12], foreR: [-0.1, 0, 0], sword: [0.6, 0, 0.3],
    };
    const flat: BP = {
      legL: [0.08, 0, -0.08], shinL: [0.15, 0, 0], legR: [0.04, 0, 0.1], shinR: [0.2, 0, 0],
      root: [1.42, 0, 0.04, 0, 0.13, -0.15], hips: [-0.1, 0, 0], chest: [-0.15, -0.1, 0.05], head: [-0.4, -0.6, 0.1], jaw: [0.45, 0, 0],
      armL: [-2.75, 0, 0.1], foreL: [-0.1, 0, 0], armR: [-0.3, 0, 0.7], foreR: [-0.2, 0, 0], sword: [0.9, 0, 0.5],
      tabard: [-1.0, 0, 0], plume: [-0.8, 0.3, 0],
    };
    timeline(b, t, [
      [0, B_ST],
      [0.2, { ...B_ST, chest: [-0.4, 0.15, 0.08], hips: [-0.08, 0, 0], head: [-0.6, 0.2, 0], jaw: [0.7, 0, 0], armR: [-0.6, 0, 0.7], foreR: [-0.5, 0, 0], armL: [-0.05, 0, -0.1], legL: [0.12, 0, -0.04], legR: [-0.12, 0, 0.04], shinR: [0.1, 0, 0] }, outExpo],
      [0.55, { ...B_ST, root: [0, 0, 0, 0, -0.1, -0.02], chest: [0.2, 0, 0.04], head: [0.3, 0.1, 0.05], jaw: [0.3, 0, 0], armR: [0.1, 0, 0.1], foreR: [-0.05, 0, 0], sword: [0.4, 0, 0.2], legL: [-0.28, 0, -0.05], shinL: [0.5, 0, 0], legR: [-0.25, 0, 0.05], shinR: [0.5, 0, 0] }, inOut],
      [0.85, kneel, inQuad],
      [0.98, { ...kneel, root: [0, 0, 0, 0, -0.42, -0.04], chest: [0.38, -0.1, 0.05], head: [0.65, 0.1, 0.1] }, outQuad],
      [1.45, { ...kneel, chest: [0.22, -0.05, 0.05], head: [0.5, 0.05, 0.08] }, inOut],
      [2.05, flat, inQuad],
      [2.9, { ...flat, root: [1.44, 0, 0.04, 0, 0.12, -0.15], head: [-0.42, -0.62, 0.1] }, outBack],
    ], LAG);
    // The banner: reeling back with the blow, held up through the kneel (the butt planted, the fist sliding down
    // the pole), then swinging back over the falling body to lie across its back.
    const tx = keys(t, [[0, 0], [0.22, -0.16, outExpo], [0.6, 0.06], [0.9, 0.0], [1.45, 0.04], [1.7, -0.35, inQuad], [2.2, -1.62, inQuad], [2.35, -1.52, outQuad], [2.55, -1.6, inOut]]);
    const tz = keys(t, [[0, 0], [0.22, 0.06], [0.9, 0.04], [1.45, 0.06], [2.2, 0.12, inQuad]]);
    aimPole(b, tx, tz);
    slidePole(b, 0, 0.44 * span(t, 0.6, 0.85, inQuad) * (1 - span(t, 1.5, 2.1)), 0.5 * span(t, 1.5, 2.2, inQuad));
    // The cloth: limp through the kneel, streaming as the pole falls, then slapped flat across the body.
    const fallK = span(t, 1.5, 2.2, inQuad) * (1 - span(t, 2.2, 2.3));
    const slap = kick(t, 2.2, 2.4, 6);
    const lie = span(t, 2.18, 2.3);
    cloth(b,
      -tx * (1 - lie) * 0.9 - 0.5 * fallK + 0.25 * slap * lie + 0.05 * lie,
      -0.35 * fallK - 0.3 * kick(t, 2.25, 2.4, 6) * lie + 0.1 * lie,
      -0.4 * fallK - 0.35 * kick(t, 2.3, 2.6, 6) * lie + 0.15 * lie,
      0.2 * kick(t, 2.25, 2, 5));
    b.trophyL.rot(-tx * 0.9 + 0.4 * kick(t, 2.2, 1.6, 4), 0, 0.2 * kick(t, 0.22, 1.5, 4));
    b.trophyR.rot(-tx * 0.9 + 0.35 * kick(t, 2.22, 1.6, 4), 0, -0.2 * kick(t, 0.25, 1.5, 4));
    // The ink pool shrinks and dries.
    b.aura.scale(Math.max(0.01, 1 - span(t, 0.8, 2.6, inQuad)));
  }, (t) => thud(t, [[0.85, 0.04], [2.05, 0.04], [2.2, 0.02]]));

/**
 * Arenga: it heaves the banner up, slams the butt into the ground, then hauls the whole pole high overhead,
 * chest thrown out and jaw agape, sword raised; the cloth cracks open and stands out, then settles.
 */
const rally = (): Clip<BB> =>
  clip<BB>('rally', 1.2, false, (b, t) => {
    const high: BP = {
      root: [0, 0, 0, 0, 0.02, 0], hips: [-0.08, 0, 0], chest: [-0.3, 0.1, 0], head: [-0.55, 0.15, 0], jaw: [0.75, 0, 0],
      armL: [-1.75, 0, 0.1], foreL: [-0.75, 0, 0], armR: [-0.4, 0, 1.25], foreR: [-0.6, 0, 0], sword: [-0.9, 0, 0],
      legL: [-0.1, 0, -0.08], legR: [0.08, 0, 0.08], shinL: [0.05, 0, 0], shinR: [0.05, 0, 0], plume: [0.25, 0, 0],
    };
    timeline(b, t, over(B_ST, [
      [0, {}],
      [0.22, { root: [0, 0, 0, 0, 0.02, 0], chest: [-0.1, 0.1, 0], head: [-0.15, 0, 0], armL: [-0.45, 0, -0.05], foreL: [-0.55, 0, 0], armR: [0.1, 0, 0.1] }, outQuad],
      [0.36, { root: [0, 0, 0, 0, -0.08, 0.02], hips: [0.1, 0, 0], chest: [0.3, -0.05, 0], head: [0.25, 0, 0], armL: [0.1, 0, -0.05], foreL: [-0.15, 0, 0], legL: [-0.3, 0, -0.06], shinL: [0.45, 0, 0], legR: [-0.15, 0, 0.06], shinR: [0.4, 0, 0] }, inCubic],
      [0.6, high, outBack],
      [0.95, { ...high, chest: [-0.26, 0.1, 0], head: [-0.5, 0.15, 0], armL: [-1.7, 0, 0.1] }],
      [1.2, {}, inOut],
    ]), LAG);
    // Heaved up, slammed down (the butt strikes the ground at 0.36), then borne high.
    slidePole(b, 0, keys(t, [[0, 0], [0.22, 0.12, outQuad], [0.36, -0.1, inCubic], [0.48, -0.08], [0.6, 0.0], [1.2, 0]]), 0);
    const slam = kick(t, 0.36, 3, 8);
    aimPole(b, keys(t, [[0, 0], [0.22, -0.04], [0.36, 0.03, inCubic], [0.6, -0.08, outBack], [0.95, -0.06], [1.2, 0, inOut]]) + 0.04 * slam, 0.03 * slam);
    // The cloth: flung back as it is heaved, it cracks on the slam, then stands out on the rising pole and settles.
    // The cloth: flung back as it is heaved, it cracks on the slam; as the pole goes up it whips out toward the
    // foe (the sigil thrown at them), snaps back and ripples flat.
    const whip = (t0: number): number => keys(t, [[t0, 0], [t0 + 0.12, -1, outQuad], [t0 + 0.24, 0.45, inOut], [t0 + 0.38, -0.15, inOut], [t0 + 0.55, 0, inOut]]);
    cloth(b,
      0.07 + 0.15 * envelope(t, 0.05, 0.22, 0.25, 0.36) - 0.2 * slam + 0.3 * whip(0.46),
      0.04 + 0.12 * envelope(t, 0.1, 0.26, 0.3, 0.4) - 0.25 * kick(t, 0.4, 3, 8) + 0.3 * whip(0.52),
      0.05 + 0.18 * envelope(t, 0.12, 0.3, 0.32, 0.42) - 0.3 * kick(t, 0.44, 3, 8) + 0.4 * whip(0.58),
      0.2 * kick(t, 0.62, 2, 5));
    b.trophyL.rot(-0.35 * slam + 0.3 * kick(t, 0.6, 1.6, 4), 0, 0.15 * kick(t, 0.62, 1.5, 4));
    b.trophyR.rot(-0.3 * slam + 0.28 * kick(t, 0.62, 1.6, 4), 0, -0.15 * kick(t, 0.64, 1.5, 4));
    // The ink pool swells with the cry.
    b.aura.scale(1 + 0.35 * envelope(t, 0.36, 0.6, 0.9, 1.2) + 0.1 * Math.max(0, slam));
  }, (t) => thud(t, [[0.36, 0.04], [0.6, -0.025]]), 'Arenga');

export const bannermanClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), rally()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { bannerman: bannermanClips };
